import { confirmBox } from '../components/dialog.js';
import { renderHeader } from '../components/header.js';
import { renderNav } from '../components/nav.js';
import { loadSessions, loadSessionSets, loadExerciseMaxes, updateSet, deleteSet, deleteSession } from '../lib/grit-data.js';
import { label, viewing, isViewingSelf } from '../lib/auth.js';
import { displayNameById, ensureNames } from '../lib/exercise-names.js';
import { toast } from '../components/toast.js';
import { icon } from '../components/icons.js';
import { go } from '../router.js';

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const ICON = {
  close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>',
  star: '<svg viewBox="0 0 24 24" fill="#E6A700" stroke="none" class="inline-block w-3.5 h-3.5 align-[-2px] ml-1"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>',
};

function fmtDate(iso) {
  // iso = 'YYYY-MM-DD'; render without timezone drift
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

export function renderHistory(el) {
  let detailId = null; // session being viewed
  let editing = false; // edit mode in the detail view
  let filter = 'all'; // day-type tag filter
  let maxes = {};     // all-time max weight per exercise (for the star)
  const canEdit = isViewingSelf();

  el.innerHTML = `${renderHeader()}<div class="grit-page px-5 pt-5"><p class="text-inksoft">Loading history…</p></div>${renderNav('home')}`;

  async function paintList() {
    editing = false;
    const sessions = await loadSessions(); // lifts only; runs live on the Running tab
    const tags = [...new Set(sessions.map((s) => s.day?.name).filter(Boolean))];
    if (filter !== 'all' && !tags.includes(filter)) filter = 'all';
    const shown = filter === 'all' ? sessions : sessions.filter((s) => (s.day?.name || '') === filter);

    const chips = ['all', ...tags].map((t) => `<button data-tag="${t === 'all' ? 'all' : encodeURIComponent(t)}" class="flex-none grit-pill ${filter === t ? '!bg-ink !text-white' : '!bg-card'}">${t === 'all' ? 'All' : esc(t)}</button>`).join('');

    const items = shown.map((s) => `<button data-id="${s.id}" class="w-full text-left grit-card p-4 mb-2 active:scale-[0.98] transition-transform">
      <div class="flex items-center justify-between">
        <div class="min-w-0"><div class="font-semibold text-[17px] text-ink">${esc(s.day?.name || 'Workout')}</div><div class="text-[15px] text-inksoft">${fmtDate(s.date)}</div>${s.notes ? `<div class="text-[13px] text-inksoft truncate mt-0.5">${esc(s.notes)}</div>` : ''}</div>
        <div class="text-right"><span class="grit-chip-${s.status === 'completed' ? 'success' : 'accent'}">${s.status === 'completed' ? 'Done' : 'Unfinished'}</span>${s.duration_min ? `<div class="text-[12px] text-inksoft mt-1">${s.duration_min} min</div>` : ''}</div>
      </div></button>`).join('');

    el.innerHTML = `
      ${renderHeader()}
      <div class="grit-page px-5 pt-5">
        <button id="back-home" class="flex items-center gap-1 text-grit text-[17px] min-h-[44px] -ml-1 mb-1">${icon.left('w-5 h-5')} Today</button>
        <div class="grit-eyebrow">${label(viewing())}'s log</div>
        <h1 class="grit-title mt-1 mb-3">Workout history</h1>
        ${tags.length ? `<div class="flex gap-2 overflow-x-auto pb-1 -mx-5 px-5 mb-3" style="scrollbar-width:none">${chips}</div>` : ''}
        ${shown.length ? items : `
          <div class="grit-card p-6 text-center">
            <div class="font-bold text-ink">${sessions.length ? 'Nothing in this filter' : 'No workouts yet'}</div>
            <div class="text-[15px] text-inksoft mt-1">${sessions.length ? 'Try a different tag.' : "Finish a workout and it shows up here, grouped by type. Runs live on the Running tab."}</div>
          </div>`}
        <div class="h-24"></div>
      </div>
      ${renderNav('home')}
    `;
    const bh = document.getElementById('back-home'); if (bh) bh.onclick = () => go('home');
    el.querySelectorAll('[data-tag]').forEach((b) => { b.onclick = () => { filter = b.dataset.tag === 'all' ? 'all' : decodeURIComponent(b.dataset.tag); paintList(); }; });
    el.querySelectorAll('[data-id]').forEach((b) => { b.onclick = () => { detailId = b.dataset.id; paintDetail(sessions.find((s) => s.id === detailId)); }; });
  }

  async function paintDetail(session) {
    const groups = await loadSessionSets(session.id);
    const body = groups.map((g) => `
      <div class="grit-card p-4 mb-2">
        <div class="font-bold text-[15px] text-ink mb-2">${esc(displayNameById(g.exerciseId, g.name))}</div>
        ${g.sets.map((s) => editing ? `
          <div class="flex items-center gap-2 py-1.5 border-t border-hairline first:border-0">
            <span class="text-[13px] text-inksoft w-16 flex-none">Set ${s.set_number}${s.is_warmup ? ' · W' : ''}</span>
            ${(g.tracking === 'weight' || (g.tracking === 'bodyweight' && s.weight_lb != null)) ? `<input data-set="${s.id}" data-f="weight" type="number" inputmode="decimal" value="${s.weight_lb ?? ''}" placeholder="lb" class="grit-input flex-1 min-w-0 text-center py-2 font-bold" />` : ''}
            <input data-set="${s.id}" data-f="reps" type="number" inputmode="numeric" value="${s.reps ?? ''}" placeholder="${g.tracking === 'time' ? 'sec' : 'reps'}" class="grit-input flex-1 min-w-0 text-center py-2 font-bold" />
            <button data-delset="${s.id}" aria-label="Delete set" class="flex-none w-11 h-11 -mr-2 flex items-center justify-center text-inksoft">${ICON.close}</button>
          </div>` : `
          <div class="flex items-center justify-between text-[15px] py-1 border-t border-hairline first:border-0">
            <span class="text-inksoft">${s.is_warmup ? 'Warm-up' : 'Set ' + s.set_number}${s.swapped_from_exercise_id ? ' · swapped' : ''}${s.rir != null ? (s.rir > 0 ? ' · reps left' : ' · to failure') : ''}</span>
            <span class="font-bold text-ink">${g.tracking === 'time'
              ? `${s.reps ?? '--'}s`
              : g.tracking === 'bodyweight'
                ? (s.weight_lb ? `${s.weight_lb} lb × ${s.reps ?? '--'}` : `${s.reps ?? '--'} reps`)
                : `${s.weight_lb ?? '--'} lb × ${s.reps ?? '--'}`}${(!s.is_warmup && g.tracking !== 'time' && s.weight_lb != null && Number(s.weight_lb) === maxes[g.exerciseId]) ? ICON.star : ''}</span>
          </div>`).join('')}
      </div>`).join('') || '<p class="text-inksoft text-[15px]">No sets recorded.</p>';

    el.innerHTML = `
      ${renderHeader()}
      <div class="grit-page px-5 pt-5">
        <div class="flex items-center justify-between mb-3">
          <button id="back" class="text-grit text-[17px] flex items-center gap-1 min-h-[44px] -ml-1">${icon.left('w-5 h-5')} All workouts</button>
          ${canEdit ? `<button id="edit" class="text-grit text-[17px] ${editing ? 'font-semibold' : ''} min-h-[44px] px-1">${editing ? 'Done' : 'Edit'}</button>` : ''}
        </div>
        <div class="grit-eyebrow">${fmtDate(session.date)}</div>
        <h1 class="grit-title mt-1 mb-4">${esc(session.day?.name || 'Workout')}</h1>
        ${session.notes ? `<div class="grit-card p-4 mb-3"><div class="grit-eyebrow mb-1">Note</div><div class="text-[15px] text-ink">${esc(session.notes)}</div></div>` : ''}
        ${editing ? '<div class="text-[13px] text-inksoft mb-3">Fix a weight or rep count, remove a stray set, or delete the whole workout. Edits update your next recommendation.</div>' : ''}
        ${body}
        ${editing ? `<button id="del-workout" class="grit-btn-danger mt-3">Delete this workout</button>` : ''}
        <div class="h-24"></div>
      </div>
      ${renderNav('home')}
    `;
    document.getElementById('back').onclick = () => { detailId = null; paintList(); };
    const ed = document.getElementById('edit');
    if (ed) ed.onclick = () => { editing = !editing; paintDetail(session); };

    el.querySelectorAll('input[data-set]').forEach((inp) => {
      inp.onchange = async () => {
        const val = inp.value === '' ? null : Number(inp.value);
        const ok = await updateSet(inp.dataset.set, { [inp.dataset.f]: val });
        if (!ok) toast("Edit didn't save, try again", { tone: 'bad' });
        else toast('Saved');
      };
    });
    el.querySelectorAll('[data-delset]').forEach((b) => {
      b.onclick = async () => {
        if (!(await confirmBox({ title: 'Remove this set?', confirmLabel: 'Remove', danger: true }))) return;
        await deleteSet(b.dataset.delset);
        paintDetail(session);
      };
    });
    const dw = document.getElementById('del-workout');
    if (dw) dw.onclick = async () => {
      if (!(await confirmBox({ title: 'Delete this whole workout?', message: "This can't be undone.", confirmLabel: 'Delete', danger: true }))) return;
      await deleteSession(session.id);
      toast('Workout deleted');
      detailId = null;
      maxes = await loadExerciseMaxes(); // a deleted PR set can change the top weight
      paintList();
    };
  }

  (async () => { await ensureNames(viewing()); maxes = await loadExerciseMaxes(); paintList(); })();
}
